import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

// ═══ Brand ID type ═══
export type BrandId =
  | "exp"       // EngageXP (default)
  | "plus"      // Gamin PLUS
  | "engine"    // Gamin ENGINE
  | "index"     // Gamin INDEX
  | "nitro"     // Gamin NITRO
  | "akl"       // Arena King League
  | "ligagg"    // LigaGG
  | "rising-stars" // Rising Stars
  | "kaiser";   // Kaiser (founder brand)

// ═══ Brand theme definition ═══
interface BrandTheme {
  id: BrandId;
  name: string;
  dataTheme?: string;
  primaryHex: string;
}

// ═══ Brand themes — CSS values come from @engagexp/legendary-css theme CSS ═══
const brandThemes: Record<BrandId, BrandTheme> = {
  exp: {
    id: "exp",
    name: "EngageXP",
    primaryHex:        "var(--p-epic)",
  },
  plus: {
    id: "plus",
    name: "Gamin PLUS",
    dataTheme:         "plus",
    primaryHex:        "var(--primary)",
  },
  engine: {
    id: "engine",
    name: "Gamin ENGINE",
    dataTheme:         "engine",
    primaryHex:        "var(--primary)",
  },
  index: {
    id: "index",
    name: "Gamin INDEX",
    dataTheme:         "index",
    primaryHex:        "var(--primary)",
  },
  nitro: {
    id: "nitro",
    name: "Gamin NITRO",
    dataTheme:         "nitro",
    primaryHex:        "var(--p-legendary)",
  },
  akl: {
    id: "akl",
    name: "AKL",
    dataTheme:         "akl",
    primaryHex:        "var(--primary)",
  },
  ligagg: {
    id: "ligagg",
    name: "LigaGG",
    dataTheme:         "ligagg",
    primaryHex:        "var(--primary)",
  },
  "rising-stars": {
    id: "rising-stars",
    name: "Rising Stars",
    dataTheme:         "rising-stars",
    primaryHex:        "var(--primary)",
  },
  kaiser: {
    id: "kaiser",
    name: "Kaiser",
    dataTheme:         "kaiser",
    primaryHex:        "var(--primary)",
  },
};

const brandAwareTokenOverrides = [
  "--p-epic",
  "--p-epic-hover",
  "--p-epic-active",
  "--p-epic-glow",
  "--epic-purple",
  "--chart-1",
  "--chart-5",
] as const;

// ═══ Context ═══
interface BrandContextType {
  brand: BrandId;
  brandTheme: BrandTheme;
  setBrand: (id: BrandId) => void;
}

const BrandContext = createContext<BrandContextType>({
  brand: "exp",
  brandTheme: brandThemes.exp,
  setBrand: () => {},
});

export function useBrand() {
  return useContext(BrandContext);
}

export function getBrandTheme(id: BrandId): BrandTheme {
  return brandThemes[id];
}

// ═══ Provider ═══
export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrandState] = useState<BrandId>(() => {
    const stored = localStorage.getItem("sigil-brand");
    return (stored as BrandId) || "exp";
  });

  const setBrand = useCallback((id: BrandId) => {
    setBrandState(id);
    localStorage.setItem("sigil-brand", id);
  }, []);

  // Apply brand theme selectors and keep legacy epic aliases brand-aware.
  useEffect(() => {
    const theme = brandThemes[brand];
    const root = document.documentElement;

    if (theme.dataTheme) {
      root.dataset.theme = theme.dataTheme;
      root.style.setProperty("--p-epic", "var(--primary)");
      root.style.setProperty("--p-epic-hover", "var(--primary-hover)");
      root.style.setProperty("--p-epic-active", "var(--primary-active)");
      root.style.setProperty("--p-epic-glow", "var(--primary)");
      root.style.setProperty("--epic-purple", "var(--primary)");
      root.style.setProperty("--chart-1", "var(--primary)");
      root.style.setProperty("--chart-5", "var(--primary)");
      return;
    }

    delete root.dataset.theme;
    brandAwareTokenOverrides.forEach((token) => root.style.removeProperty(token));
  }, [brand]);

  return (
    <BrandContext.Provider value={{ brand, brandTheme: brandThemes[brand], setBrand }}>
      {children}
    </BrandContext.Provider>
  );
}
